import React from 'react';
import { Link } from 'react-router-dom';
import { GraduationCap, ArrowRight, CheckCircle, Shield, Star, Users, BookOpen, Target } from 'lucide-react';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { MarketingNav } from '../components/MarketingLayout';

const HERO_IMG = "https://images.unsplash.com/photo-1729040268565-6dc77b9a9eef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwyfHxpdnklMjBsZWFndWUlMjB1bml2ZXJzaXR5JTIwY2FtcHVzJTIwYXV0dW1ufGVufDB8fHx8MTc3Mzg4Nzc1M3ww&ixlib=rb-4.1.0&q=85";

const services = [
  { text: "Proactive long-range planning", icon: Target },
  { text: "Institutional-level competitive positioning analysis", icon: BookOpen },
  { text: "Coordinated communication with school counselors", icon: Users },
  { text: "Multi-advisor review of all materials", icon: Shield },
  { text: "Disciplined expectation management", icon: Star },
  { text: "Strategic positioning beginning early in high school", icon: CheckCircle },
];

const differentiators = [
  { title: "Multi-Advisor Review", desc: "Every application is examined by multiple former admissions interviewers and senior advisors." },
  { title: "Long-Range Strategy", desc: "We begin building your narrative early — not just editing essays senior year." },
  { title: "Institutional Awareness", desc: "Our team has sat in committee rooms. We know how applications are read and debated." },
];

export default function PremiumPage() {
  return (
    <div className="min-h-screen bg-white" data-testid="premium-page">
      <SEO
        title="Premium Advisory — $499/mo Ivy League Admissions Coaching"
        description="Multi-advisor Ivy League admissions coaching with essay architecture, competitive analysis, and long-range planning. Monthly retainer for families who want a managed process. $499/month."
        keywords="ivy league advisory, premium admissions coaching, harvard admissions coach, ivy league essay coach"
      />
      {/* Nav */}
      <MarketingNav />

      {/* Hero with Image */}
      <section className="pt-16 relative overflow-hidden">
        <div className="relative h-[55vh] min-h-[420px] max-h-[580px]">
          <img src={HERO_IMG} alt="University campus" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F392B] via-[#0F392B]/75 to-[#0F392B]/35" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="max-w-4xl mx-auto px-4 text-center">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/15 mb-6 backdrop-blur-sm">
                <Star className="h-3.5 w-3.5 text-[#D4AF37]" fill="#D4AF37" />
                <span className="text-xs font-medium text-white/90 tracking-wide uppercase">Premium Advisory</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.08] text-white mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                Precision for Families Who<br /><span className="italic">Expect Excellence</span>
              </h1>
              <p className="text-base md:text-lg text-white/75 max-w-2xl mx-auto leading-relaxed">
                Families who understand investment understand that process determines outcome.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <p className="text-sm font-medium text-[#D4AF37] tracking-wide uppercase mb-3">What We Provide</p>
              <h2 className="text-2xl md:text-3xl font-medium tracking-tight mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>
                Strategic advisory at every stage
              </h2>
              <div className="space-y-3">
                {services.map((s, i) => (
                  <div key={i} className="flex items-start gap-3 p-4 rounded-xl bg-[#F9F9F7] border border-[#E2E2E2] hover:shadow-md transition-shadow" data-testid={`premium-service-${i}`}>
                    <s.icon className="h-5 w-5 text-[#0F392B] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                    <span className="text-sm font-medium">{s.text}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-6">
              <div className="bg-[#0F392B] rounded-2xl p-8 md:p-10 text-white">
                <Shield className="h-8 w-8 text-[#D4AF37] mb-4" strokeWidth={1.5} />
                <h3 className="text-xl font-medium mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Our Promise</h3>
                <p className="text-white/70 text-sm leading-relaxed mb-4">
                  We do not promise guaranteed outcomes.
                </p>
                <p className="text-white text-base leading-relaxed mb-6">
                  We promise disciplined preparation, structured execution, and institutional-level strategy.
                </p>
                <p className="text-lg font-medium italic" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Prestige is not purchased.<br />It is <span className="text-[#D4AF37]">positioned</span>.
                </p>
              </div>
              <div className="bg-[#F9F9F7] rounded-2xl border border-[#E2E2E2] p-8 text-center">
                <span className="text-4xl font-semibold text-[#0F392B]" style={{ fontFamily: "'Playfair Display', serif" }}>$499</span>
                <span className="text-sm text-muted-foreground ml-1">/month</span>
                <p className="text-xs text-muted-foreground mt-2">Full strategic engagement with multi-advisor review</p>
                <Link to="/sign-up" className="block mt-4">
                  <Button className="w-full bg-[#0F392B] hover:bg-[#0F392B]/90 text-white h-11" data-testid="premium-price-cta">
                    Get Premium <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Differentiators */}
      <section className="py-16 bg-[#F9F9F7] border-t border-[#E2E2E2]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {differentiators.map((d, i) => (
              <div key={i} className="p-6 rounded-xl bg-white border border-[#E2E2E2]" data-testid={`premium-diff-${i}`}>
                <h3 className="text-base font-medium mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>{d.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{d.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-[#0F392B]">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Schedule a Consultation
          </h2>
          <p className="text-base text-white/70 mb-8 max-w-xl mx-auto">
            Premium advisory engagements begin with a confidential family consultation.
          </p>
          <Link to="/sign-up">
            <Button size="lg" className="bg-white text-[#0F392B] hover:bg-white/90 h-12 px-8 text-base shadow-xl" data-testid="premium-cta-btn">
              Get Started <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#E2E2E2] py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-2">
            <GraduationCap className="h-5 w-5 text-[#0F392B]" strokeWidth={1.5} />
            <span className="font-medium" style={{ fontFamily: "'Playfair Display', serif" }}>Ivy League Consulting</span>
          </div>
          <div className="flex items-center gap-6 text-xs text-muted-foreground">
            <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
            <Link to="/case-method" className="hover:text-foreground transition-colors">The Case Method</Link>
            <Link to="/portal" className="hover:text-foreground transition-colors">$99 Portal</Link>
            <Link to="/pricing" className="hover:text-foreground transition-colors">Packages</Link>
          </div>
          <p className="text-xs text-muted-foreground">&copy; {new Date().getFullYear()} Ivy League Consulting. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
